import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const TerminalBox = () => {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [typingIndex, setTypingIndex] = useState(0);
  const [blinkCursor, setBlinkCursor] = useState(true);
  const animationRef = useRef({ timeouts: [] });
  
  const terminalLines = [
    { text: 'compiling ideas...', delay: 1000, prefix: '>' },
    { text: 'UI rendered', delay: 800, prefix: '✔', className: 'success' },
    { text: 'Code deployed', delay: 600, prefix: '✔', className: 'success' },
    { text: 'Portfolio loaded by Shoumik', delay: 400, prefix: '→', className: 'arrow' },
  ];

  // Clear all timeouts to prevent animation issues
  const clearAllTimeouts = () => {
    animationRef.current.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    animationRef.current.timeouts = [];
  };
  
  // Add a new timeout and keep track of it
  const addTimeout = (callback, delay) => {
    const timeoutId = setTimeout(callback, delay);
    animationRef.current.timeouts.push(timeoutId);
    return timeoutId;
  };

  useEffect(() => {
    // Initial delay before typing starts
    const initialTimeout = addTimeout(() => {
      setBlinkCursor(false);
      // No need to call animateTyping here as the dependency effect will handle it
    }, 800);
    
    return () => clearAllTimeouts();
  }, []);
  
  useEffect(() => {
    if (currentLineIndex >= terminalLines.length) {
      setBlinkCursor(true);
      return;
    }
    
    const currentLine = terminalLines[currentLineIndex];
    
    if (typingIndex < currentLine.text.length) {
      // Typing animation for current line
      const typingTimeout = addTimeout(() => {
        setTypingIndex(prevIndex => prevIndex + 1);
      }, 30 + Math.random() * 50);
    } else {
      // Move to next line after delay
      const nextLineTimeout = addTimeout(() => {
        setCurrentLineIndex(prevIndex => prevIndex + 1);
        setTypingIndex(0);
      }, currentLine.delay);
    }
    
    return () => clearAllTimeouts();
  }, [currentLineIndex, typingIndex]);

  return (
    <StyledTerminalBox className="terminal-box">
      {terminalLines.map((line, lineIndex) => {
        // Only show lines that have been reached in the animation
        if (lineIndex > currentLineIndex) return null;
        
        const displayText = lineIndex === currentLineIndex
          ? line.text.substring(0, typingIndex)
          : line.text;
          
        // Show solid cursor during typing, blinking cursor only after completion
        const isTyping = lineIndex === currentLineIndex && typingIndex < line.text.length;
        const isCompleted = currentLineIndex >= terminalLines.length && lineIndex === terminalLines.length - 1;
        
        return (
          <p key={lineIndex} className={lineIndex === currentLineIndex ? 'current-line' : 'completed-line'}>
            <span className={line.className || 'prompt'}>{line.prefix}</span>{' '}
            {displayText}
            {isTyping && <span className="solid-cursor">▋</span>}
            {isCompleted && <span className="blinking-cursor">▋</span>}
          </p>
        );
      })}
    </StyledTerminalBox>
  );
};

export default TerminalBox;

const StyledTerminalBox = styled.div`
  background-color: #0e0e0e;
  color: #ccc;
  font-family: 'Fira Code', 'Courier New', monospace;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  box-shadow: 0 0 30px rgba(0,0,0,0.4), inset 0 0 2px rgba(255,255,255,0.1);
  line-height: 1.8;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255,255,255,0.05);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #4fc3f7, transparent);
    opacity: 0.3;
  }
  
  /* Fixed dimensions to prevent layout shifts */
  width: 100%;
  max-width: 520px;
  height: 180px; /* Fixed height */
  box-sizing: border-box;
  
  p {
    margin: 0.2rem 0;
    opacity: 0;
    transform: translateY(10px);
    animation: fadeIn 0.5s forwards;
    min-height: 1.8rem; 
    white-space: pre-wrap; /* Preserve whitespace but wrap text */
    height: 1.8rem; /* Fixed height for each line */
    line-height: 1.8rem; /* Match line height with height */
    position: relative; /* For consistent positioning */
  }
  
  /* Add extra padding to the top of the first line */
  p:first-child {
    margin-top: 0.75rem;
  }

  .completed-line {
    opacity: 0.8;
  }

  .current-line {
    opacity: 1;
  }

  .prompt {
    color: #4fc3f7;
  }

  .success {
    color: #81c784;
    font-weight: bold;
  }

  .arrow {
    color: #fbc02d;
    font-weight: bold;
  }

  .highlight {
    color: #ffffff;
    font-weight: bold;
    text-shadow: 0 0 10px rgba(255,255,255,0.3);
  }

  .solid-cursor {
    display: inline-block;
    width: 0.6rem;
    height: 1.2em;
    background-color: #4a5bef; /* Updated to match application accent color */
    vertical-align: middle;
    margin-left: 2px;
    opacity: 0.9;
  }

  .blinking-cursor {
    display: inline-block;
    width: 0.6rem;
    height: 1.2em;
    background-color: #4a5bef; /* Updated to match application accent color */
    animation: blink 1s step-end infinite;
    vertical-align: middle;
    margin-left: 2px;
    opacity: 0.9;
  }

  /* Mac-style window buttons */
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 16px;
    width: 12px;
    height: 12px;
    background-color: #ff5f56;
    border-radius: 50%;
    box-shadow: 
      20px 0 0 #ffbd2e,
      40px 0 0 #27c93f;
  }


  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
